/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect } from 'react';
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell as MuiTableCell,
  TableBody,
  Typography,
  useMediaQuery,
  useTheme,
  styled,
  Link,
} from '@mui/material';
import {
  getSalesOrders,
  selectSalesOrderByAgreementId,
  selectSalesOrderRequestStatusByAgreementId,
} from './storage';
import { TestIds } from '../../testIds';
import { useSelector } from 'react-redux';
import { Loader } from '../../components/Loader';
import { useAppDispatch } from '../../store/rootStore';
import dayjs from 'dayjs';
import { formatUnitDisplay } from './SalesLine';
import { useNavigate } from 'react-router-dom';
import MobileSalesOrderCard from './SalesOrdersMobile';
import CollapseSection from './components/CollapseSection';

const SalesOrdersContainer = styled('div')({});

const SalesOrdersView = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: '8px',
});

const TableCell = styled(MuiTableCell)(({ theme }) => ({
  '&.MuiTableCell-body': {
    padding: '16px 16px 16px 0',
  },
  '&.MuiTableCell-alignRight': {
    padding: '16px 0px 16px 16px',
  },
  '& .material-symbols-outlined': {
    color: theme.colors.primary500,
  },
}));

interface SalesOrdersProps {
  agreementId: string;
}

const SalesOrders = ({ agreementId }: SalesOrdersProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const data = useSelector(selectSalesOrderByAgreementId(agreementId));
  const status = useSelector(selectSalesOrderRequestStatusByAgreementId(agreementId));
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getSalesOrders(agreementId));
  }, []);

  const orderDetailsNavigate = (
    e: React.MouseEvent<HTMLElement, MouseEvent> | null,
    path: string,
    orderId: string,
  ) => {
    e?.preventDefault();
    navigate(`${path}${orderId}`, {
      state: {
        orderId,
      },
    });
  };

  const renderMobile = () => {
    return (
      <SalesOrdersView data-testid={TestIds.feat.agreement.salesOrder.table}>
        {data?.map((order) => (
          <MobileSalesOrderCard
            key={order.id}
            order={order}
            onViewOrder={(id) => orderDetailsNavigate(null, '/order/', id)}
          />
        ))}
      </SalesOrdersView>
    );
  };

  const render = () => (
    <TableContainer data-testid={TestIds.feat.agreement.salesOrder.table}>
      <Table aria-label='Sales orders table'>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Product</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.map((order) => (
            <TableRow
              key={order.id}
              data-testid={TestIds.feat.agreement.salesOrder.tableItem}
              data-id={order.id}
            >
              <TableCell component='th' scope='row'>
                {dayjs(order.date).format('DD/MM/YYYY')}
              </TableCell>
              <TableCell>{order.status}</TableCell>
              <TableCell>{order.id}</TableCell>
              <TableCell>
                {order.products.map(({ productDescription }) => (
                  <React.Fragment key={productDescription}>
                    <span key={productDescription}>{productDescription}</span>
                    <br />
                  </React.Fragment>
                ))}
              </TableCell>
              <TableCell>
                {order.products.map(({ amount, unit }) => (
                  <React.Fragment key={amount}>
                    <span>{`${amount}${formatUnitDisplay(unit)}`}</span>
                    <br />
                  </React.Fragment>
                ))}
              </TableCell>
              <TableCell>
                <Link variant='body2' onClick={(e) => orderDetailsNavigate(e, '/order/', order.id)}>
                  View order
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderBody = React.useMemo(() => {
    if (!data && status === 'pending') {
      return <Loader title='Loading sales orders' />;
    }
    if ((!data || data.length === 0) && status === 'fulfilled') {
      return (
        <Typography variant='body2'>
          You do not currently have any Sales Orders for this Sales Agreement.
        </Typography>
      );
    }
    if (isSmall) {
      return renderMobile();
    } else {
      return render();
    }
  }, [data, status, isSmall]);

  return (
    <SalesOrdersContainer>
      <CollapseSection defaultOpen={true} title='Sales orders'>
        {renderBody}
      </CollapseSection>
    </SalesOrdersContainer>
  );
};

export default SalesOrders;
